/**
 * Хаягийн 3 үеийг (аймаг/хот → сум/дүүрэг → баг/хороо) нэг стандарт бүтэцтэй
 * болгох цэвэр функцууд.
 *
 * Хаяг 3 өөр эх сурвалжаас ирдэг тул бүтэц нь зөрдөг:
 *  1. Газрын зураг (`search-nearby-address`) — `_id` талбартай,
 *  2. "Гараас бичих" таб (`AddressUnitsSelector`) — antd Option-ий props,
 *  3. Эрлианы тогтмол хаяг (Step2 дэх `UB`) — `key`/`value` талбартай.
 *
 * Серверт `deliveryCode` болгож явуулахад 3 үе бүрд `addressId` байх ёстой тул
 * энд нормчилж, дараа нь `isValidAddressUnits`-аар шалгана.
 */

/** Хаягийн үе бүрийн серверт хүлээгдэх дараалал */
const ORDER_BY_TYPE = { aimaghot: 100, sumduureg: 200, baghoroo: 300 };

/** Хаягийн код нь цифрээс тогтдог ('11', '1101', '110101') */
const ID_PATTERN = /^\d{2,}$/;

/**
 * `addressId`-г олох: өөрийнх нь утга байхгүй бол зөвхөн код шиг харагдах
 * (цифрээс тогтох) талбарыг л сэлбэж авна. `value` нь хаягийн НЭР байх тохиолдол
 * бий тул нэрийг код болгож явуулахгүй.
 */
const resolveAddressId = (unit, fallbackId) => {
  if (unit.addressId) return String(unit.addressId);

  const candidate = [fallbackId, unit._id, unit.value, unit.key].find(
    (item) => item != null && ID_PATTERN.test(String(item))
  );

  return candidate == null ? '' : String(candidate);
};

/** Нэг үеийг нормчилно. Эх объектыг хөндөхгүй, код олдоогүй бол `null`. */
const normalizeUnit = (unit, fallbackId) => {
  if (!unit || typeof unit !== 'object') return null;

  const addressId = resolveAddressId(unit, fallbackId);
  if (!addressId) return null;

  // `_id` нь серверт шаардлагагүй (mongo id), нэр/дараалал нь шаардлагатай
  const { _id, ...rest } = unit;

  return {
    ...rest,
    addressId,
    name: unit.name ?? unit.children ?? unit.value,
    order: ORDER_BY_TYPE[unit.type] ?? unit.order,
  };
};

/**
 * Хаягийн массивыг [аймаг, сум, баг] гэсэн 3 үе болгож нормчилно.
 * Гурвуулаа бүрэн биш бол хоосон массив буцаана.
 */
export const normalizeAddressUnits = (units, fallbackBagHorooId) => {
  if (!Array.isArray(units)) return [];

  const bagRaw = units.find((unit) => unit?.type === 'baghoroo') ?? units[2];

  const result = [
    normalizeUnit(units[0]),
    normalizeUnit(units[1]),
    normalizeUnit(bagRaw, fallbackBagHorooId),
  ].filter(Boolean);

  return result.length === 3 ? result : [];
};

/** Серверт явуулахад бэлэн эсэх — 3 үе, төрөл зөв, код бүрэн */
export const isValidAddressUnits = (units) =>
  Array.isArray(units) &&
  units.length === 3 &&
  units[0]?.type === 'aimaghot' &&
  units[1]?.type === 'sumduureg' &&
  units[2]?.type === 'baghoroo' &&
  units.every((unit) => Boolean(unit?.addressId));

/**
 * Газрын зургийн хаягийн массивыг ("search-nearby-address"-ийн `address`)
 * хаягийн 3 үе болгоно. Зөвхөн aimag/sum/bag үеүүдийг авна.
 */
export const mapAddressToUnits = (address = []) =>
  normalizeAddressUnits(
    (Array.isArray(address) ? address : [])
      .filter((item) => ORDER_BY_TYPE[item?.type])
      .map((item) => ({
        type: item.type,
        order: ORDER_BY_TYPE[item.type],
        name: item.name,
        value: item.name,
        addressId: item.addressId ?? (item._id == null ? undefined : String(item._id)),
      }))
  );

/** Хаягийн 3 үеийг "Улаанбаатар, БЗД, 1-р хороо" болгож нэгтгэнэ */
export const formatAddressUnits = (units = []) =>
  (Array.isArray(units) ? units : [])
    .map((unit) => unit?.name)
    .filter(Boolean)
    .join(', ');
